import {Box, Button, ButtonProps, CircularProgress} from '@mui/material';
import {forwardRef} from 'react';

export type AppButtonVariant =
    | 'primary'
    | 'secondary'
    | 'destructive'
    | 'destructiveOutlined'
    | 'ghost';

export type AppButtonProps = {
    variant?: AppButtonVariant;
    loading?: boolean;
} & Omit<ButtonProps, 'variant' | 'color'>;

const mapVariant = (
    v: AppButtonVariant
): Pick<ButtonProps, 'variant' | 'color'> => {
    switch (v) {
        case 'primary':
            return {variant: 'contained', color: 'primary'};
        case 'secondary':
            return {variant: 'outlined', color: 'primary'};
        case 'destructive':
            return {variant: 'contained', color: 'error'};
        case 'destructiveOutlined':
            return {variant: 'outlined', color: 'error'};
        case 'ghost':
            return {variant: 'text', color: 'inherit'};
        default:
            return {variant: 'contained', color: 'primary'};
    }
};

const spinnerSizeFor = (size: ButtonProps['size']) => {
    if (size === 'small') return 16;
    if (size === 'large') return 22;
    return 18;
};

export const AppButton = forwardRef<HTMLButtonElement, AppButtonProps>(
    function AppButton(props, ref) {
        const {
            variant: appVariant = 'primary',
            loading = false,
            disabled,
            children,
            sx,
            size = 'medium',
            ...rest
        } = props;
        const {variant: muiVariant, color} = mapVariant(appVariant);
        const spinnerSize = spinnerSizeFor(size);

        return (
            <Button
                ref={ref}
                variant={muiVariant}
                color={color}
                size={size}
                disabled={disabled || loading}
                aria-busy={loading || undefined}
                sx={[
                    loading && {position: 'relative'},
                    ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
                ]}
                {...rest}
            >
                {loading ? (
                    <>
                        <CircularProgress
                            size={spinnerSize}
                            thickness={4}
                            sx={{
                                color: 'currentColor',
                                position: 'absolute',
                                left: '50%',
                                top: '50%',
                                mt: `${-spinnerSize / 2}px`,
                                ml: `${-spinnerSize / 2}px`,
                            }}
                        />
                        <Box
                            component="span"
                            sx={{
                                visibility: 'hidden',
                                display: 'inline-flex',
                                alignItems: 'center',
                                columnGap: 1,
                            }}
                        >
                            {children}
                        </Box>
                    </>
                ) : (
                    children
                )}
            </Button>
        );
    }
);
